import { Children, cloneElement, DetailedReactHTMLElement, HTMLAttributes, PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';
import { Label } from '@phoenix/design-system/frontend/ui/form/label/label';
import { Hint, HintType } from '@phoenix/design-system/frontend/ui/form/hint';

function isDetailedReactHTMLElement<P extends HTMLAttributes<unknown>, T extends HTMLElement>(o: any): o is DetailedReactHTMLElement<P, T> {
  return 'type' in o;
}

export interface HookFormRadioRadioGroupProps {
  className?: string;
  classNameFields?: string;
  name: string;
  label?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  errorPosition?: 'top' | 'bottom';
  id?: string;
}

export const HookFormRadioRadioGroup = ({
  children,
  className,
  classNameFields,
  name,
  label = '',
  description,
  required = false,
  disabled = false,
  errorPosition = 'bottom',
  id = name,
  ...restProps
}: PropsWithChildren<HookFormRadioRadioGroupProps>) => {
  const {
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation('shared');
  const hint = errors[name]?.message && {
    message: errors[name]?.message,
    type: 'error' as HintType,
    dataTestId: `${name}ValidationErrorMessage`,
  };
  return (
    <fieldset className={cx('mb-4 desktop:mb-m', className)} {...restProps}>
      <label className={cx('flex flex-col stihl-text-body-sm-reg', className)} htmlFor={id}>
        {label && <Label disabled={disabled} text={`${label} ${required ? '' : t('shared_formControlLabel_optionalSuffix')}`} />}
        {description && <div className="mb-2 mt-1 stihl-text-body-xs-reg">{description}</div>}
      </label>
      {hint && errorPosition === 'top' && (
        <div>
          <Hint {...hint} />
        </div>
      )}
      <div className={cx('flex pl-0.5', classNameFields)}>
        {Children.map(children, (child) => {
          if (child && isDetailedReactHTMLElement<HookFormRadioRadioGroupProps, HTMLElement>(child)) {
            return cloneElement(child, {
              id: child.props.id,
              name,
              className: cx('mr-m mb-0', child?.props?.className),
              withinRadioGroup: true,
              hint: null,
            });
          }
          return child;
        })}
      </div>
      {hint && errorPosition === 'bottom' && (
        <div>
          <Hint {...hint} />
        </div>
      )}
    </fieldset>
  );
};

export default HookFormRadioRadioGroup;
